import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Loader2, Check } from 'lucide-react';
import { Mascot } from '@/components/Mascot';
import { supabase } from '@/lib/supabase';
import { useTelegram } from '@/hooks/useTelegram';
import { useWeekReport } from '@/hooks/useWeekReport';
import type { DayRow } from '@/utils/weekInsights';

// ════════════════════════════════════════════════════════════════
// Разбор недели по отметкам состояния.
// Глубина зависит от того, сколько дней заполнено: обещание «полный
// разбор за полную неделю» должно быть видно и выполнено буквально.
// ════════════════════════════════════════════════════════════════

const MOOD_COLOR = [
  'bg-line',                 // нет отметки
  'bg-danger/70',            // 1
  'bg-over/60',              // 2
  'bg-ink-dim/50',           // 3
  'bg-emerald-glow/50',      // 4
  'bg-emerald-glow',         // 5
];

const WEEKDAY = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const WEEKDAY_FULL = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const SHARE_NOTES_KEY = 'week_analysis_share_notes';

function Strip({ rows, pick }: { rows: DayRow[]; pick: (d: DayRow) => number | null }) {
  return (
    <div className="flex gap-1">
      {rows.map((d) => {
        const v = pick(d);
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <div className={`w-full h-7 rounded-md ${MOOD_COLOR[v ?? 0]}`} />
            <span className="text-[9px] text-ink-dim">
              {WEEKDAY[new Date(`${d.date}T12:00:00`).getDay()]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function WeekReview() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { report, rows, loading } = useWeekReport();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Выбор запоминается: спрашивать одно и то же каждую неделю — навязчиво
  const [shareNotes, setShareNotes] = useState(
    () => localStorage.getItem(SHARE_NOTES_KEY) === '1',
  );

  function toggleShareNotes() {
    haptic('light');
    setShareNotes((v) => {
      localStorage.setItem(SHARE_NOTES_KEY, v ? '0' : '1');
      return !v;
    });
  }

  // Отправляем ТОЛЬКО цифры и теги. Свободные записи («пара слов») остаются
  // на устройстве: туда человек пишет личное, и отдавать это внешней модели
  // без нужды не стоит — на связки они всё равно не влияют.
  async function askSlava() {
    if (!report || sending) return;
    haptic('medium');
    setSending(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('week-analysis', {
        body: {
          filledDays: report.filledDays,
          facts: report.facts,
          connections: report.connections.map((c) => ({ text: c.text, basis: c.basis })),
          days: rows.map((d) => ({
            date: d.date,
            mood: d.mood,
            sleep: d.sleep,
            calories: d.calories,
            eveningCalories: d.eveningCalories,
            tags: d.tags,
            // Записи уходят только с явного согласия
            note: shareNotes ? d.note : null,
          })),
        },
      });
      if (fnErr) throw fnErr;
      if ((data as any)?.error) throw new Error((data as any).message ?? (data as any).error);
      nav('/chat');
    } catch (e: any) {
      setError(e?.message ?? 'Не получилось. Попробуй ещё раз через минуту.');
    } finally {
      setSending(false);
    }
  }

  if (loading || !report) {
    return (
      <div className="px-5 pb-28 max-w-md mx-auto pt-6 flex items-center gap-2 text-ink-dim text-sm">
        <Loader2 size={14} className="animate-spin" />
        Собираю неделю...
      </div>
    );
  }

  const maxCal = Math.max(1, ...rows.map((r) => r.calories));

  return (
    <div className="px-5 pb-28 max-w-md mx-auto">
      <div className="sticky top-0 z-30 -mx-5 px-5 pt-4 pb-3 mb-5 backdrop-blur-xl bg-bg/75 border-b border-line/20 flex items-center gap-2">
        <button onClick={() => { haptic('light'); nav(-1); }} className="active:opacity-70">
          <ChevronLeft size={20} className="text-ink-muted" />
        </button>
        <div className="font-display text-lg">Разбор недели</div>
      </div>

      {/* Реплика Славы */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-4 mb-4 flex gap-3"
      >
        <Mascot pose={report.concern ? 'support' : 'calm'} compact className="w-12 h-12 shrink-0" />
        <p className="text-sm leading-relaxed whitespace-pre-line">{report.slavaLine}</p>
      </motion.div>

      {/* При тревожном флаге еду не разбираем совсем */}
      {!report.concern && report.level !== 'none' && (
        <>
          {/* Лента недели */}
          <div className="card p-4 mb-4 space-y-4">
            <div>
              <div className="text-[11px] text-ink-dim mb-2">Как проходили дни</div>
              <Strip rows={rows} pick={(d) => d.mood} />
            </div>

            <div>
              <div className="text-[11px] text-ink-dim mb-2">Как спалось</div>
              <Strip rows={rows} pick={(d) => d.sleep} />
            </div>

            <div>
              <div className="text-[11px] text-ink-dim mb-2">Сколько съедено</div>
              <div className="flex gap-1 items-end h-12">
                {rows.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col justify-end h-full">
                    <div
                      className="w-full rounded-t-md bg-sky-glow/40"
                      style={{ height: `${Math.max(3, (d.calories / maxCal) * 100)}%` }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Факты */}
          {report.facts.length > 0 && (
            <div className="card p-4 mb-4">
              <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-2">Коротко о неделе</div>
              <ul className="space-y-1.5">
                {report.facts.map((f) => (
                  <li key={f} className="text-sm text-ink-muted flex gap-2">
                    <span className="text-emerald-glow shrink-0">·</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Твоими словами — записи и теги за неделю.
              Главная ценность этих строк не для модели, а для самого человека:
              прочитать «поругались вечером» рядом со своим же плотным ужином
              и понять всё самому. Раньше они лежали в базе и никому
              не показывались — то есть собирались зря. */}
          {rows.some((d) => d.note || d.tags?.length) && (
            <div className="card p-4 mb-4">
              <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-3">Твоими словами</div>
              <div className="space-y-3">
                {rows
                  .filter((d) => d.note || d.tags?.length)
                  .map((d) => (
                    <div key={d.date} className="flex gap-3">
                      <div
                        className={`w-1 rounded-full shrink-0 ${MOOD_COLOR[d.mood ?? 0]}`}
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <div className="text-[10px] text-ink-dim">
                          {WEEKDAY_FULL[new Date(`${d.date}T12:00:00`).getDay()]}
                          {d.mood ? ` · день на ${d.mood}` : ''}
                        </div>
                        {d.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {d.tags.map((t) => (
                              <span
                                key={t}
                                className="text-[10px] text-ink-muted px-2 py-0.5 rounded-full border border-line"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                        {d.note && <p className="text-sm text-ink-muted mt-1 leading-snug">{d.note}</p>}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Связки */}
          {report.connections.length > 0 && (
            <div className="space-y-3 mb-4">
              <div className="text-[11px] text-ink-dim uppercase tracking-wider px-1">Что я заметил</div>
              {report.connections.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="card p-4"
                >
                  <p className="text-sm leading-relaxed">{c.text}</p>
                  {/* Основание показываем всегда: семь дней — это наблюдение,
                      а не доказательство, и человек должен это видеть */}
                  <div className="mt-2 text-[10px] text-ink-dim">Основание: {c.basis}</div>
                </motion.div>
              ))}

              {/* Записи — личное. Решает человек, а не мы за него.
                  Выключено по умолчанию. */}
              {rows.some((d) => d.note) && (
                <button
                  onClick={toggleShareNotes}
                  className="w-full flex items-start gap-2.5 px-1 text-left active:opacity-70"
                >
                  <span
                    className={[
                      'mt-0.5 w-4 h-4 rounded-md border shrink-0 flex items-center justify-center',
                      shareNotes ? 'bg-emerald-glow/20 border-emerald-glow/60' : 'border-line',
                    ].join(' ')}
                  >
                    {shareNotes && <Check size={11} className="text-emerald-glow" />}
                  </span>
                  <span className="text-[11px] text-ink-dim leading-snug">
                    Дать Дофи прочитать мои записи. Без этого он видит только оценки и теги —
                    разбор всё равно получится, просто без твоих слов.
                  </span>
                </button>
              )}

              {/* Разбор со Славой. Связки уже найдены кодом — модель их только
                  объясняет, поэтому выдумать новых закономерностей не может. */}
              <button
                onClick={askSlava}
                disabled={sending}
                className="w-full p-4 rounded-2xl bg-emerald-glow/15 border border-emerald-glow/40 text-left active:scale-[0.99] transition-transform disabled:opacity-60"
              >
                <div className="flex items-center gap-3">
                  <Mascot pose="think" compact className="w-10 h-10 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {sending ? 'Дофи разбирает...' : 'Разобрать с Дофи'}
                    </div>
                    <div className="text-[11px] text-ink-dim">
                      {sending ? 'Это займёт несколько секунд' : 'Не тратит сообщения из дневного лимита'}
                    </div>
                  </div>
                  {sending && <Loader2 size={16} className="animate-spin text-emerald-glow shrink-0" />}
                </div>
              </button>

              {error && <div className="text-[11px] text-danger px-1">{error}</div>}
            </div>
          )}

          {/* Ступень до полного разбора */}
          {report.level !== 'full' && (
            <div className="card p-4 border-emerald-glow/25">
              <p className="text-sm text-ink-muted leading-relaxed">
                Это короткий разбор — по {report.filledDays} из 7 дней. Чем полнее неделя,
                тем больше связок я могу увидеть. Полный разбор открывается, когда отмечены все семь.
              </p>
            </div>
          )}
        </>
      )}

      <p className="text-[10px] text-ink-dim leading-relaxed mt-5 px-1">
        Это наблюдения за одну неделю, а не медицинские выводы. Проверяй их на себе —
        закономерности становятся надёжными за месяц-два наблюдений.
      </p>
    </div>
  );
}
